import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { CopyIcon, CheckIcon } from "lucide-react";
import { ExclamationCircleIcon } from "@heroicons/react/24/outline";
import { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { IntlShape } from "react-intl";
import { cn } from "@/lib/utils";
import { formatNumberLocale } from "@/utils/helpers";

/**
 * Copy icon component with visual feedback
 * Displays copy/check icon state but doesn't handle click (parent handles it)
 */
export const CopyIconIndicator = ({ copied }: { copied: boolean }) => {
  return (
    <>
      {copied ? (
        <CheckIcon className="h-3.5 w-3.5 text-green-600 shrink-0" />
      ) : (
        <CopyIcon className="h-3.5 w-3.5 shrink-0" />
      )}
    </>
  );
};

interface CompactFieldRowProps {
  label: string;
  value: number | string;
  intl: IntlShape;
  formatType?: "currency" | "decimal" | "none";
  isUnavailable?: boolean;
  tooltip?: string;
  className?: string;
  rawCopyValue?: number | string;
}

/**
 * Compact field row component with label, value, and copy functionality
 * The entire row is clickable to copy the value
 */
export const CompactFieldRow = ({
  label,
  value,
  intl,
  formatType = "currency",
  isUnavailable = false,
  tooltip,
  className,
  rawCopyValue,
}: CompactFieldRowProps) => {
  const { t } = useTranslation(["components"]);
  const [copied, setCopied] = useState(false);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const formattedValue =
    formatType === "currency"
      ? formatNumberLocale(intl, value as number, "currency")
      : formatType === "decimal"
        ? formatNumberLocale(intl, value as number, "decimal")
        : value;

  const handleCopy = async () => {
    try {
      const copySource = rawCopyValue ?? value;
      const textToCopy = typeof copySource === "number" ? copySource.toString() : String(copySource);
      await navigator.clipboard.writeText(textToCopy);
      setCopied(true);

      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      timeoutRef.current = setTimeout(() => setCopied(false), 2000);
    } catch {
      // Silently ignore clipboard errors
    }
  };

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  if (isUnavailable) {
    return (
      <div className={cn("flex items-center justify-between gap-2 py-1.5 px-1", className)}>
        <div className="flex items-center gap-1.5 min-w-0 flex-1">
          <span className="text-xs text-muted-foreground truncate">{label}</span>
        </div>
        <div>
          {tooltip && (
            <Tooltip>
              <TooltipTrigger asChild>
                <div className="text-xs text-amber-500 flex items-center gap-1">
                  <ExclamationCircleIcon className="h-3.5 w-3.5 text-amber-500 shrink-0" />
                  <span>{t("pricedetails.unavailable")}</span>
                </div>
              </TooltipTrigger>
              <TooltipContent className="max-w-[320px]">
                <p>{tooltip}</p>
              </TooltipContent>
            </Tooltip>
          )}
        </div>
      </div>
    );
  }

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <div
          className={cn(
            "flex items-center justify-between gap-2 py-1.5 group hover:bg-slate-50 px-1 rounded-md cursor-pointer transition-colors",
            className
          )}
          onClick={handleCopy}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              handleCopy();
            }
          }}
          aria-label={`Copy ${label}: ${formattedValue}`}
        >
          <span className="text-xs text-muted-foreground truncate min-w-0 flex-1">{label}</span>
          <div className="flex items-center gap-1.5 shrink-0">
            <span className="text-sm font-medium text-foreground">{formattedValue}</span>
            <CopyIconIndicator copied={copied} />
          </div>
        </div>
      </TooltipTrigger>
      <TooltipContent>
        <p>
          {copied
            ? t("pricedetails.copy.copied")
            : t("pricedetails.copy.clicktocopy", { label })}
        </p>
      </TooltipContent>
    </Tooltip>
  );
};
